/* @flow */

import { observable, when } from 'mobx';

import { PortalPromotion, PortalPromotionsCarousel, GlobalMessageBanner } from 'models/kentico';

type KontentDeliveryClientOptions = {
  projectId: ?string,
  typeResolvers?: TypeResolver<any>[],
};

export type ContentTypeName =
  | 'portalPromotionsCarousel'
  | 'portalPromotion'
  | 'portalGlobalMessage';

type ContentTypeMap = {
  portalPromotionsCarousel: PortalPromotionsCarousel,
  portalPromotion: PortalPromotion,
  portalGlobalMessage: GlobalMessageBanner,
};

export const CONTENT_TYPE_NAMES = {
  portalPromotionsCarousel: 'portalPromotionsCarousel',
  portalPromotion: 'portalPromotion',
  portalGlobalMessage: 'portalGlobalMessage',
};

export class TypeResolver<T> {
  mapFn: (data: Object, modularContent?: Object) => T;
  contentTypeName: string;
  typeName: string;

  constructor(
    contentTypeName: ContentTypeName,
    typeName: string,
    mapFunction: (data: Object, modularContent?: Object) => T
  ) {
    this.contentTypeName = contentTypeName;
    this.typeName = typeName;
    this.mapFn = mapFunction;
  }
}

export type KenticoDeliveryClientConfigurationStatus = 'configured' | 'configuring' | 'no-key';
class KontentDeliveryClient {
  _projectId: string;
  _typeResolvers: TypeResolver<any>[] = [];

  language = 'en-EU';
  globalMessageTypeName = 'myer_global_message';

  @observable configured: KenticoDeliveryClientConfigurationStatus = 'configuring';

  constructor(options: KontentDeliveryClientOptions) {
    const { projectId, typeResolvers } = options;

    if (projectId) {
      this._projectId = projectId;
    } else {
      console.error('Kentico Kontent Project ID not provided.');
      this.configured = 'no-key';
      return;
    }

    if (typeResolvers) {
      this._typeResolvers.push(...typeResolvers);
    }

    this.configured = 'configured';
  }

  _generateUrl = (path: string) => `https://deliver.kontent.ai/${this._projectId}/${path}`;

  async _get(path: string, queryParameters?: { [string]: string }) {
    await when(() => this.configured === 'configured');

    const headers = new Headers();
    headers.set('X-KC-SDKID', 'custom;custom;custom');

    const _queryParameters = {
      // TODO: This needs to be correctly specified when languages are revisited
      language: this.language,
      ...queryParameters,
    };
    let queryString = Object.entries(_queryParameters).reduce(
      (_queryString, [key, value], index) =>
        // flow-disable-next-line
        `${_queryString}${index === 0 ? '' : '&'}${key}=${value}`,
      '?'
    );

    if (queryString.includes(this.globalMessageTypeName)) {
      queryString = `${queryString}&elements.enabled[contains]=yes`;
    }

    const fetchResult = await fetch(this._generateUrl(`${path}${queryString}`), {
      method: 'GET',
      headers,
    });

    return fetchResult.json();
  }

  async getItems<T: ContentTypeName>(
    type: T,
    filters?: { [f: string]: string }
  ): Promise<$ElementType<ContentTypeMap, T>[]> {
    const primaryTypeResolver = this._typeResolvers.find(
      resolver => resolver.contentTypeName === type
    );

    if (!primaryTypeResolver) {
      throw new Error(`Cannot find type resolver for type '${type}'`);
    }

    const _filters = {
      ...filters,
      'system.type': primaryTypeResolver.typeName,
    };

    const getResult = await this._get('items', _filters);

    if (getResult && getResult.items && typeof getResult.items.map === 'function') {
      return getResult.items.map(item => {
        const typeResolver = this._typeResolvers.find(
          resolver => resolver.typeName === item.system.type
        );

        if (typeResolver) {
          return typeResolver.mapFn(item, getResult.modular_content);
        }
      });
    }

    return [];
  }
}

export default class KenticoService {
  deliveryClient = new KontentDeliveryClient({
    projectId: process.env.REACT_APP_KONTENT_PROJECT_ID,
    typeResolvers: [
      new TypeResolver(
        CONTENT_TYPE_NAMES.portalPromotion,
        'portal_promotion',
        (data, modularContent) => new PortalPromotion(data, modularContent)
      ),
      new TypeResolver(
        CONTENT_TYPE_NAMES.portalPromotionsCarousel,
        'portal_promotions_carousel',
        (data, modularContent) => new PortalPromotionsCarousel(data, modularContent)
      ),
      new TypeResolver(
        CONTENT_TYPE_NAMES.portalGlobalMessage,
        'myer_global_message',
        (data, modularContent) => new GlobalMessageBanner(data, modularContent)
      ),
    ],
  });
}
